import './Header.scss';
import './DonationButton.scss';
import './Navigation/Navigation.scss';
import { bool2str, forAll, linkToButton, toggleAriaExpanded } from '@stst/Javascript/util';
import { Popup } from '@stst/Javascript/util/popup';

let headerPopup;

/**
 * @return {boolean}
 */
const isMobile = () => (e => e && e.offsetParent !== null)(document.querySelector('.navigation-primary__mobile-toggle'));

/**
 * @param {HTMLAnchorElement} anchor
 * @param {boolean} state
 */
const setSubmenuState = (anchor, state) => {
    if (!anchor)
        return;
    anchor.setAttribute('aria-expanded', bool2str(state));
    anchor.closest('li').classList.toggle('navigation-primary__item--expanded', state);
};

/**
 * @param {boolean} state
 */
const toggleHeaderPopup = state => {
    const header = document.querySelector('.page-header-backdrop');
    if (!headerPopup) {
        headerPopup = new Popup(header, {
            expandedAttribute: 'data-nav-expanded',
            addButtonListener: false,
            closeOnOutsideClick: true,
            disableScrolling: false,
        });
        header.addEventListener('data-nav-expanded-open', () => {
            header.classList.add('page-header-backdrop--visible');
            document.body.classList.add('navigation-extended');
        });
        header.addEventListener('data-nav-expanded-close', () => {
            header.classList.remove('page-header-backdrop--visible');
            document.body.classList.remove('navigation-extended');
            setSubmenuState(header.querySelector('[aria-expanded=true]'), false);
            toggleAriaExpanded(
                { target: header.querySelector('.navigation-primary__mobile-toggle') },
                false
            );
        });
    }
    headerPopup.open = state;
};

const resetMobileMenu = () => {
    forAll('.navigation-primary__item--level-1 > a',
        e => e.setAttribute('aria-expanded', bool2str(e.parentNode.classList.contains('navigation-primary__item--active')))
    );
};

const setBodyHeight = () => {
    const bodyEl = document.querySelector('body');
    // reset backdrop before adding a new one
    bodyEl.style.setProperty('--header-backdrop-height', '0px');
    const viewportHeight = document.documentElement.clientHeight;
    const headerWithoutNav =  document.querySelector('header').style.getPropertyValue('--header-desktop-image');
    const headerWithNav = (document.querySelector('header').scrollHeight + 20);
    const navPopup = isMobile()
        ? document.querySelector('.navigation-primary').scrollHeight
        : document.querySelector('.navigation-primary__submenu-toggle[aria-expanded="true"] + .js-first-level-container').scrollHeight;
    // set new numbers for backdrop
    if (headerWithNav > viewportHeight) {
        bodyEl.style.setProperty('--header-desktop-height', `${headerWithNav}px`);
        bodyEl.style.setProperty('--header-backdrop-height', `${navPopup + 20}px`);
    } else {
        bodyEl.style.setProperty('--header-desktop-height', `${viewportHeight}px`);
        bodyEl.style.setProperty('--header-backdrop-height', `${viewportHeight - headerWithoutNav + 20}px`);
    }
};

/**
 * @param {{target: Element}} ev
 */
const toggleMobileMenu = ev => {
    if (ev.target.getAttribute('aria-expanded') !== 'true') {
        resetMobileMenu();
    } else {
        forAll('.navigation-primary__item--level-1 > a',
            e => e.setAttribute('aria-expanded', 'false'));
    }
    toggleAriaExpanded(ev);
    toggleHeaderPopup(ev.target.getAttribute('aria-expanded') === 'true');
    setBodyHeight();
};

/**
 * @param {{target: Element}} ev
 */
const toggleDesktopMenu = ev => {
    const otherSubmenuOpen = document.querySelector('.navigation-primary__submenu-toggle[aria-expanded="true"]');
    if (otherSubmenuOpen && (otherSubmenuOpen !== ev.target)) {
        otherSubmenuOpen.setAttribute('aria-expanded', 'false');
    }
    toggleAriaExpanded(ev);
    toggleHeaderPopup(ev.target.getAttribute('aria-expanded') === 'true');
    if (document.querySelector('.navigation-primary__submenu-toggle[aria-expanded="true"]')) {
        setBodyHeight();
    }
};

/**
 * @param {HTMLDivElement} el
 * @return {function(FocusEvent)}
 */
const handleSubMenuLeave = el => () => {
    setTimeout(() => {
        if (!el.contains(document.activeElement)) {
            const anchor = el.closest('li').querySelector('button');
            if (!isMobile()) {
                setSubmenuState(anchor, false);
                toggleHeaderPopup(false);
            }
        }
    }, 0);
};

const initSearchButton = () => {
    let searchButton = document.querySelector('.page-header-search-button');
    if (searchButton) {
        searchButton = linkToButton(searchButton);
        const modal = document.querySelector('.search-modal');
        searchButton.setAttribute('aria-controls', modal.id);
        let currentlyFocusedElement = null;
        let modalOpen = false;
        if (!modal) {
            return;
        }
        const setModalState = state => {
            modal.setAttribute('aria-hidden', bool2str(!state));
            searchButton.setAttribute('aria-expanded', bool2str(state));
            modalOpen = state;
        };
        const openSearchModal = () => {
            currentlyFocusedElement = document.activeElement;
            setModalState(true);
            modal.querySelector('input').focus();
        };
        const closeSearchModal = () => {
            setModalState(false);
            if (currentlyFocusedElement) {
                currentlyFocusedElement.focus();
            }
        };
        modal.querySelector('.search-modal__backdrop').addEventListener('click', closeSearchModal);
        searchButton.addEventListener('click', openSearchModal);
        window.addEventListener('keydown', ev => {
            if (modalOpen && ev.key === 'Escape') {
                closeSearchModal();
            }
        });
        document.addEventListener('focusin', () => {
            if (modalOpen && !modal.contains(document.activeElement)) {
                closeSearchModal();
            }
        });
    }
};


const init = () => {
    if (isMobile()) {
        let menuToggleButton = document.querySelector('.navigation-primary__mobile-toggle');
        if (menuToggleButton) {
            menuToggleButton = linkToButton(menuToggleButton);
            menuToggleButton.setAttribute('aria-expanded', 'false');
            menuToggleButton.setAttribute('aria-controls', 'navigation-primary__menu');
            menuToggleButton.addEventListener('click', toggleMobileMenu);
        }
    }
    if (!isMobile()) {
        forAll('.navigation-primary__submenu-toggle',
            el => el.addEventListener('click', toggleDesktopMenu));

        forAll('.navigation-primary__item-container',
            el => el.addEventListener('focusout', handleSubMenuLeave(el)));

        document.querySelector('body').style.setProperty('--header-desktop-image', `${document.querySelector('header').getBoundingClientRect().height}px`);
    }

    initSearchButton();
};

document.addEventListener('DOMContentLoaded', init);
